// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Settings hub layout + tab sync to URL
// 2. Separation of Concerns - Delegates prompts/word correction to child components
// 3. Modular Architecture - Uses shared hub nav config
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/system-settings-management.md
/**
 * Settings hub content only; primary navigation lives in App.jsx (same URLs).
 * Hub tab order for system categories: config/fixedSystemSettingsNav.js (not API).
 * Routes: /chat-prompts, /qa-prompts, /word-correction, /settings-category/:category.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import SettingsList from './SettingsList';
import SettingEditor from './SettingEditor';
import ScalarSettingEditor from './ScalarSettingEditor';
import TranslationGlossarySettings from './TranslationGlossarySettings';
import SystemPromptManagement from './SystemPromptManagement';
import QAPromptsManagement from './QAPromptsManagement';
import WordCorrectionManagement from './WordCorrectionManagement';
import ConfirmDialog from '../common/ConfirmDialog';
import AlertDialog from '../common/AlertDialog';
import systemSettingsService from '../../services/systemSettingsService';
import {
  computeSettingsHubNavItems,
  SETTINGS_CATEGORY_UPDATE_PERMISSION,
} from '../../config/settingsHubNav';
import { useAuth } from '../../contexts/AuthContext';
import TuneIcon from '@mui/icons-material/Tune';
import PageSectionHeader from '../common/PageSectionHeader';

const NexusSettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, data: null });
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'info',
  });
  const [scalarEditorOpen, setScalarEditorOpen] = useState(false);
  const [scalarEditingSetting, setScalarEditingSetting] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState({ open: false, setting: null });

  const hubNavItems = useMemo(
    () => computeSettingsHubNavItems(hasPermission),
    [hasPermission],
  );

  const activeIndex = useMemo(() => {
    const i = hubNavItems.findIndex((item) => item.path === location.pathname);
    return i >= 0 ? i : 0;
  }, [hubNavItems, location.pathname]);

  useEffect(() => {
    if (hubNavItems.length === 0) {
      return;
    }
    const match = hubNavItems.some((item) => item.path === location.pathname);
    if (!match) {
      navigate(hubNavItems[0].path, { replace: true });
    }
  }, [hubNavItems, location.pathname, navigate]);

  const safeIndex =
    hubNavItems.length === 0 ? 0 : Math.min(activeIndex, hubNavItems.length - 1);
  const currentItem = hubNavItems[safeIndex];
  const settingsCategory =
    currentItem?.kind === 'settings_category' ? currentItem.category : null;

  const canUpdateSettings = useMemo(() => {
    if (!settingsCategory) {
      return false;
    }
    const code = SETTINGS_CATEGORY_UPDATE_PERMISSION[settingsCategory];
    return code ? hasPermission(code) : false;
  }, [settingsCategory, hasPermission]);

  /** Voice processing runtime is .env-only; UI is read-only for values. */
  const voiceProcessingEnvOnly = settingsCategory === 'voice_processing';
  const canEditCategoryValues = canUpdateSettings && !voiceProcessingEnvOnly;

  const loadSettings = useCallback(async (category) => {
    setLoading(true);
    setError(null);
    try {
      const data = await systemSettingsService.getByCategory(category);
      setSettings(data);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings');
      setSettings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!settingsCategory) {
      setSettings([]);
      return;
    }
    loadSettings(settingsCategory);
  }, [settingsCategory, loadSettings]);

  const handleAddOption = (setting) => {
    setEditingSetting(setting);
    setEditingOption(null);
    setEditorOpen(true);
  };

  const handleEditScalarValue = (setting) => {
    setScalarEditingSetting(setting);
    setScalarEditorOpen(true);
  };

  const handleScalarSave = async (parsedValue) => {
    if (!scalarEditingSetting?.id) {
      return;
    }
    try {
      await systemSettingsService.update(scalarEditingSetting.id, {
        setting_value: parsedValue,
      });
      setScalarEditorOpen(false);
      setScalarEditingSetting(null);
      if (settingsCategory) {
        loadSettings(settingsCategory);
      }
      setAlertDialog({
        open: true,
        title: 'Success',
        message: 'Setting updated successfully',
        severity: 'success',
      });
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || 'Failed to update setting';
      setAlertDialog({
        open: true,
        title: 'Error',
        message: String(msg),
        severity: 'error',
      });
    }
  };

  const handleScalarCancel = () => {
    setScalarEditorOpen(false);
    setScalarEditingSetting(null);
  };

  const handleRequestRestoreVoiceDefault = (setting) => {
    setRestoreConfirm({ open: true, setting });
  };

  const confirmRestoreVoiceDefault = async () => {
    const target = restoreConfirm.setting;
    if (!target?.id) {
      setRestoreConfirm({ open: false, setting: null });
      return;
    }
    try {
      await systemSettingsService.restoreVoiceProcessingDefault(target.id);
      setRestoreConfirm({ open: false, setting: null });
      if (settingsCategory) {
        loadSettings(settingsCategory);
      }
      setAlertDialog({
        open: true,
        title: 'Success',
        message: 'Restored to the default value from platform seed.',
        severity: 'success',
      });
    } catch (err) {
      setRestoreConfirm({ open: false, setting: null });
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to restore default',
        severity: 'error',
      });
    }
  };

  const handleEditOption = (setting, option) => {
    setEditingSetting(setting);
    setEditingOption(option);
    setEditorOpen(true);
  };

  const handleDeleteOption = (setting, optionValue) => {
    setConfirmDialog({
      open: true,
      data: { setting, optionValue },
    });
  };

  const confirmDeleteOption = async () => {
    const { setting, optionValue } = confirmDialog.data;
    try {
      await systemSettingsService.deleteOption(setting, optionValue);
      if (settingsCategory) {
        loadSettings(settingsCategory);
      }
      setAlertDialog({
        open: true,
        title: 'Success',
        message: 'Option deleted successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error deleting option:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to delete option',
        severity: 'error',
      });
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingOption) {
        await systemSettingsService.updateOption(
          editingSetting,
          editingOption.value,
          data,
        );
      } else {
        await systemSettingsService.addOption(editingSetting, data);
      }
      setEditorOpen(false);
      setEditingSetting(null);
      setEditingOption(null);
      if (settingsCategory) {
        loadSettings(settingsCategory);
      }
    } catch (err) {
      console.error('Error saving option:', err);
      throw err;
    }
  };

  const handleCancel = () => {
    setEditorOpen(false);
    setEditingSetting(null);
    setEditingOption(null);
  };

  const handleUpdateSetting = async (settingId, payload) => {
    try {
      await systemSettingsService.update(settingId, payload);
      if (settingsCategory) {
        loadSettings(settingsCategory);
      }
      setAlertDialog({
        open: true,
        title: 'Success',
        message: 'Setting updated successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error updating setting:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to update setting',
        severity: 'error',
      });
    }
  };

  if (hubNavItems.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 60px)' }}>
        <Alert severity="info">
          You do not have access to any settings sections. Contact an administrator if you need access.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 60px)' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {currentItem?.kind === 'chat_prompts' && <SystemPromptManagement />}

      {currentItem?.kind === 'qa_prompts' && <QAPromptsManagement />}

      {currentItem?.kind === 'word_correction' && <WordCorrectionManagement />}

      {currentItem?.kind === 'settings_category' && settingsCategory && (
        <Box>
          <PageSectionHeader
            icon={<TuneIcon />}
            title={currentItem.label}
            description={
              settingsCategory === 'voice_processing'
                ? 'Values shown match the server .env (environment). Edit the backend .env and restart API + voice workers to change behavior — this screen is read-only for voice processing.'
                : settingsCategory === 'translation_glossary'
                  ? 'Glossary per upload language → English (same languages as voice upload).'
                  : 'System settings for this category'
            }
          />
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography color="text.secondary">Loading settings...</Typography>
            </Box>
          ) : (
            <Paper sx={{ p: 3 }}>
              {voiceProcessingEnvOnly && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Voice processing (Whisper, upload limits, enhancement prompts, etc.) is configured only via
                  the server <strong>.env</strong>. The list below shows <strong>effective</strong> values from
                  the running app — editing here is disabled.
                </Alert>
              )}
              {settingsCategory === 'translation_glossary' ? (
                <TranslationGlossarySettings
                  settings={settings}
                  canUpdateSettings={canUpdateSettings}
                  onAddOption={handleAddOption}
                  onEditOption={handleEditOption}
                  onDeleteOption={handleDeleteOption}
                  onUpdateSetting={handleUpdateSetting}
                />
              ) : (
                <SettingsList
                  settings={settings}
                  onAddOption={handleAddOption}
                  onEditOption={handleEditOption}
                  onDeleteOption={handleDeleteOption}
                  onUpdateSetting={handleUpdateSetting}
                  onEditScalarValue={handleEditScalarValue}
                  canUpdateSettings={canEditCategoryValues}
                  showVoiceProcessingRestore={false}
                  onRestoreVoiceDefault={handleRequestRestoreVoiceDefault}
                />
              )}
            </Paper>
          )}
        </Box>
      )}

      {editorOpen && (
        <SettingEditor
          isOpen={editorOpen}
          setting={editingSetting}
          option={editingOption}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}

      <ScalarSettingEditor
        isOpen={scalarEditorOpen}
        setting={scalarEditingSetting}
        onSave={handleScalarSave}
        onCancel={handleScalarCancel}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, data: null })}
        onConfirm={confirmDeleteOption}
        title="Delete Option"
        message="Are you sure you want to delete this option? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        confirmColor="error"
      />

      <ConfirmDialog
        open={restoreConfirm.open}
        onClose={() => setRestoreConfirm({ open: false, setting: null })}
        onConfirm={confirmRestoreVoiceDefault}
        title="Restore default value"
        message={
          restoreConfirm.setting
            ? `Reset "${restoreConfirm.setting.display_name}" to the platform seed default? Your current value will be replaced.`
            : ''
        }
        confirmText="Restore"
        cancelText="Cancel"
        severity="info"
        confirmColor="primary"
      />

      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ open: false, title: '', message: '', severity: 'info' })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Container>
  );
};

export default NexusSettingsPage;
